import { ChevronLeft, X, ExternalLink, AlertCircle, Zap, Lock, RefreshCw } from "lucide-react";
import {
  UseFormRegister,
  FieldValues,
  UseFormHandleSubmit,
  FieldErrors,
  Control,
} from "react-hook-form";
import { FormInput } from "../components/forms/Input";
import { FormSelect } from "../components/forms/Select";
import { FormNumberInput } from "../components/forms/NumberInput";
import { FormPassword } from "../components/forms/Password";
import { FormTextArea } from "../components/forms/TextArea";
import { ConnectionDefinition } from "../types/integrations";

import { FormSwitch } from "../components/forms/Switch";
import MarkdownRenderer from "../components/MarkdownRenderer";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

interface IProps {
  definition: ConnectionDefinition;
  onClose: () => void;
  onSubmit: (data: { [K: string]: string }) => void;
  onBack?: () => void;
  register: UseFormRegister<FieldValues>;
  control?: Control<FieldValues, any>
  handleSubmit: UseFormHandleSubmit<FieldValues>;
  errors: FieldErrors<FieldValues>;
  error?: string;
  showNameInput?: boolean;
  isLoading?: boolean;
  isConnectionSelected?: boolean;
  helperLink?: string;
  platformImageUrl?: string;
  showAvatars?: boolean;
  companyName?: string;
  isWhiteLabel?: boolean | null;
}

export const ConnectionFormSection = ({
  definition,
  onClose,
  onBack,
  register,
  control,
  handleSubmit,
  onSubmit,
  errors,
  error,
  showNameInput,
  isLoading,
  isConnectionSelected,
  helperLink,
  platformImageUrl,
  showAvatars,
  companyName,
  isWhiteLabel,
}: IProps) => {

  const formData = showNameInput
    ? [
        {
          name: "NAME",
          type: "input",
          label: "Name",
          placeholder: "My Connection",
          rules: {
            required: "This field is required",
          },
        },
        ...definition?.frontend?.connectionForm?.formData,
      ]
    : definition?.frontend?.connectionForm?.formData;

  return (
    <div className="flex flex-col w-full flex-1 min-h-0">
      {/* Back/Close Row - always pinned at top */}
      <div className="w-full px-5 pt-4 pb-2 flex-shrink-0">
        <div className="flex justify-between w-full items-center">
          <div
            className="flex cursor-pointer gap-0 items-center text-[13px] text-muted-foreground hover:text-foreground transition-colors duration-150"
            onClick={onBack}
          >
            {!isConnectionSelected && !isLoading && (
              <div className="flex gap-0 items-center">
                <ChevronLeft size={16} />
                <span>Back</span>
              </div>
            )}
          </div>
          <X
            size={13}
            className="cursor-pointer text-muted-foreground hover:text-foreground transition-colors duration-150"
            style={{ width: '13px', height: '13px', minWidth: '13px', minHeight: '13px' }}
            onClick={onClose}
          />
        </div>
      </div>

          {/* Scrollable Form Area */}
          <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin px-5 flex flex-col">
            <form className="w-full flex-1 flex flex-col" onSubmit={handleSubmit(onSubmit)}>
              {/* Icon + Title + Trust/Fields - wrapped for vertical centering on OAuth */}
              <div className={cn(
                "w-full flex flex-col",
                showAvatars && (!formData || formData.length === 0) ? "flex-1 justify-center" : ""
              )}>
                {/* Icon + Title */}
                {showAvatars ? (
                  <div className="w-full flex flex-col items-center gap-4 pb-6">
                    {platformImageUrl ? (
                      <Avatar className="h-12 w-12 border border-border">
                        <AvatarImage
                          src={platformImageUrl}
                          alt={definition?.frontend?.spec?.title}
                          className="p-1.5"
                        />
                        <AvatarFallback>{definition?.frontend?.spec?.title?.[0]}</AvatarFallback>
                      </Avatar>
                    ) : (
                      <img
                        src={definition?.frontend?.spec?.image}
                        alt={definition?.frontend?.spec?.title}
                        className="w-12 h-12"
                      />
                    )}
                    <h1 className="text-center text-[15px] font-medium px-4 text-foreground leading-snug">
                      {companyName ? `${companyName} uses One to connect your ${definition?.frontend?.spec?.title} account` : `Connect your ${definition?.frontend?.spec?.title} account`}
                    </h1>
                  </div>
                ) : (
                  <div className="w-full flex flex-col items-center gap-3 pb-4">
                    <img
                      src={definition?.frontend?.spec?.image}
                      alt={definition?.frontend?.spec?.title}
                      className="w-12 h-12"
                    />
                    <h1 className="text-center text-[18px] font-semibold">
                      {definition?.frontend?.spec?.title}
                    </h1>
                    <p className="text-center text-[13px] text-muted-foreground">
                      {definition?.frontend?.spec?.category}
                    </p>
                  </div>
                )}
                <div className="flex flex-col gap-5 pb-6 w-full">
                {/* Trust sections for OAuth with no form fields */}
                {showAvatars && (!formData || formData.length === 0) && (
                  <div className="flex flex-col gap-6 px-1">
                    <div className="flex items-start gap-3 w-full">
                      <Zap size={18} className="flex-shrink-0 mt-0.5 text-muted-foreground" />
                      <div className="flex flex-col gap-0.5">
                        <span className="font-normal text-[13px] text-foreground">Authenticate instantly</span>
                        <p className="text-[12px] text-muted-foreground">Secure OAuth flow authenticates directly with your provider in seconds.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 w-full">
                      <RefreshCw size={18} className="flex-shrink-0 mt-0.5 text-muted-foreground" />
                      <div className="flex flex-col gap-0.5">
                        <span className="font-normal text-[13px] text-foreground">Always connected</span>
                        <p className="text-[12px] text-muted-foreground">Authentication tokens refresh automatically so your integration stays active.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 w-full">
                      <Lock size={18} className="flex-shrink-0 mt-0.5 text-muted-foreground" />
                      <div className="flex flex-col gap-0.5">
                        <span className="font-normal text-[13px] text-foreground">Enterprise security</span>
                        <p className="text-[12px] text-muted-foreground">Your credentials remain encrypted and are never exposed.</p>
                      </div>
                    </div>
                  </div>
                )}
                {/* Form Fields */}
                {formData && formData.length > 0 && (
                  <div className="w-full flex flex-col gap-0">
                    {formData?.map((form: any) => {
                    return (
                      <div
                        className={form.hidden ? "pt-0" : "pt-5"}
                        key={form.name}
                      >
                        {form?.type === "input" && (
                          <FormInput
                            name={form.name}
                            label={form.label}
                            placeholder={form.placeholder}
                            rules={form.rules}
                            register={register}
                            errors={errors}
                            disabled={form.disabled}
                            value={form.value}
                            hidden={form.hidden}
                          />
                        )}
                        {form?.type === "select" && (
                          <FormSelect
                            name={form.name}
                            label={form.label}
                            rules={form.rules}
                            register={register}
                            errors={errors}
                            options={form.options}
                            hidden={form.hidden}
                          />
                        )}
                        {form?.type === "number" && (
                          <FormNumberInput
                            name={form.name}
                            label={form.label}
                            placeholder={form.placeholder}
                            rules={form.rules}
                            register={register}
                            errors={errors}
                            max={form.max}
                            min={form.min}
                            step={form.step}
                            value={form.value}
                          />
                        )}
                        {form?.type === "switch" && (
                          <FormSwitch
                            name={form.name}
                            label={form.label}
                            rules={form.rules}
                            register={register}
                            errors={errors}
                            control={control}
                            hidden={form.hidden}
                          />
                        )}
                        {form?.type === "password" && (
                          <FormPassword
                            name={form.name}
                            label={form.label}
                            placeholder={form.placeholder}
                            rules={form.rules}
                            register={register}
                            errors={errors}
                          />
                        )}
                        {form?.type === "textarea" && (
                          <FormTextArea
                            name={form.name}
                            label={form.label}
                            placeholder={form.placeholder}
                            rules={form.rules}
                            register={register}
                            errors={errors}
                          />
                        )}
                      </div>
                    );
                  })}
                  </div>
                )}
                
                {definition?.frontend?.spec?.markdown && (
                  <div className="max-w-full w-full border border-dashed rounded-[12px] px-2 border-border">
                    <MarkdownRenderer
                      markdown={definition.frontend.spec.markdown}
                    />
                  </div>
                )}
              </div>

              {/* Connect Button Section */}
              {showAvatars ? (
                // OAuth: Button at bottom with loading state
                <div className="flex flex-col gap-4 pb-4">
                  <div className="flex flex-col items-center gap-4 w-full">
                    {error && (
                      <Alert variant="destructive" className="rounded-[12px] w-full mb-2">
                        <AlertCircle className="h-4 w-4 flex-shrink-0" />
                        <AlertDescription>
                          {error}
                        </AlertDescription>
                      </Alert>
                    )}

                    <Button
                      isLoading={isLoading}
                      type="submit"
                      className="w-full text-[13px]"
                    >
                      {isLoading ? "Connecting" : "Connect"}
                    </Button>

                    {isLoading && (
                      <div className="flex items-center justify-center gap-1 text-[13px] text-muted-foreground">
                        <p>Having trouble connecting?</p>
                        <p
                          className="cursor-pointer underline font-bold text-foreground"
                          onClick={isConnectionSelected ? onClose : onBack}
                        >
                          Try again
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                // Non-OAuth: Original layout
                <>
                  {error && (
                    <Alert variant="destructive" className="rounded-[12px] mb-4">
                      <AlertCircle className="h-4 w-4 flex-shrink-0" />
                      <AlertDescription>
                        {error}
                      </AlertDescription>
                    </Alert>
                  )}

                  <Button
                    isLoading={isLoading}
                    type="submit"
                    className="w-full text-[13px] mb-4"
                  >
                    {isLoading ? "Connecting" : "Connect"}
                  </Button>
                </>
              )}

              {/* Non-OAuth: Loading state below button */}
              {!showAvatars && isLoading && (
                <div className="flex items-center justify-center gap-1 pb-4 text-[13px] mt-auto text-muted-foreground">
                  <p>Having trouble connecting?</p>
                  <p
                    className="cursor-pointer underline font-bold text-foreground"
                    onClick={isConnectionSelected ? onClose : onBack}
                  >
                    Try again
                  </p>
                </div>
              )}
              </div>{/* end centering wrapper */}
            </form>
          </div>
      
      {/* Fixed Helper Banner */}
      {helperLink && (
        <div
          className={cn(
            "flex-shrink-0 flex items-center justify-center gap-2 px-5 py-2.5 border-t border-border",
          )}
        >
          <span className="text-[10px] font-mono text-muted-foreground">
            Need help?
          </span>
          <span className="text-[10px] font-mono text-muted-foreground">·</span>
          <a
            href={helperLink}
            target="_blank"
            rel="noreferrer"
            className="text-[10px] font-mono text-muted-foreground hover:text-foreground inline-flex items-center gap-1 transition-colors duration-150"
          >
            Connection Guide
            <ExternalLink size={10} />
          </a>
        </div>
      )}
    </div>
  );
};
